import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { currencySymbol, fmtNumber } from '../../utils/region';
import { brandOr } from '../../utils/brand';

// AI Working Reports — per-client task board (P1 UI). Lists accounting clients with their services
// + task roll-up; click a client to see / generate / update its generated tasks. Supersedes the
// old Compliance deadlines view. See docs/ai-working-reports-design.md.

// 10.5 → 11.5px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
// Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this:
// the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular weight, where
// they were dark grey in bold, 10px above and below on one 16px line, a 37px band, white where it was light grey.
// 10 → 5px under the heading text, so it sits that much nearer its grey line and the band is 5px shorter
// (report c151d269: "Bring the heading text 5 pixels closer to the grey line below. Do this for every page." —
// Active Jobs' th is the model; the 10px above the text, the sides, the size and the line are as they were).
const th: React.CSSProperties = { fontSize: 11.5, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 10px 5px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
const td: React.CSSProperties = { fontSize: 12.5, padding: '8px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };
const btn: React.CSSProperties = { padding: '7px 13px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', cursor: 'pointer' };
const fmtDate = (ymd: string) => { const p = String(ymd || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : (ymd || '—'); };
const money = (n: any) => currencySymbol() + fmtNumber(n || 0);
const CT_STATUS: Record<string, { bg: string; c: string }> = { draft: { bg: 'var(--c-gray-100)', c: 'var(--text-secondary)' }, sent: { bg: '#FFFBEB', c: '#B45309' }, signed: { bg: '#ECFDF5', c: '#15803D' }, void: { bg: '#FEF2F2', c: '#DC2626' } };

function Contracts({ clientId }: { clientId: string }) {
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const load = () => { api.contractList(clientId).then((d: any) => setRows(d.rows || [])).catch(() => {}); };
  useEffect(load, [clientId]); // eslint-disable-line
  const create = async () => { setBusy(true); try { await api.contractCreate(clientId); load(); } catch (e: any) { window.alert(e.message); } finally { setBusy(false); } };
  const send = async (c: any) => { try { const r: any = await api.contractSend(c.id); const url = window.location.origin + r.url; await navigator.clipboard.writeText(url).catch(() => {}); window.prompt('Signing link (copied to clipboard) — send this to the client:', url); load(); } catch (e: any) { window.alert(e.message); } };
  const copyLink = async (c: any) => { const url = window.location.origin + '/contract/' + c.sign_token; await navigator.clipboard.writeText(url).catch(() => {}); window.prompt('Signing link (copied):', url); };
  return (
    <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>Contracts</span>
        <button onClick={create} disabled={busy} style={{ padding: '5px 11px', fontSize: 12, fontWeight: 700, borderRadius: 7, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}><i className="ti ti-file-plus" /> {busy ? '…' : 'Draft from services'}</button>
        {rows.map(c => {
          const s = CT_STATUS[c.status] || CT_STATUS.draft;
          return (
            <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: s.bg, color: s.c }}>{c.status}</span>
              <span style={{ color: 'var(--text-tertiary)' }}>{money(c.fee_total)}/mo</span>
              {c.status === 'draft' && <button onClick={() => send(c)} style={{ border: 'none', background: 'none', color: 'var(--accent)', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>Send for signing</button>}
              {c.status === 'sent' && <button onClick={() => copyLink(c)} style={{ border: 'none', background: 'none', color: 'var(--accent)', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>Copy link</button>}
              {c.status === 'signed' && <span style={{ color: '#15803D' }}>✓ {c.signer_name}</span>}
            </span>
          );
        })}
      </div>
    </div>
  );
}
const CYCLE_LABEL: Record<string, string> = { monthly: 'Monthly', half_year: 'Half-year', annual: 'Annual', one_off: 'One-off' };
const ACTION_LABEL: Record<string, string> = { ai_auto: 'AI auto', need_client_input: 'Client input', need_human_sign: 'Human sign', need_paper_handover: 'Paper' };

// At-a-glance flags after the company name: VAT · WHT · employees · monthly/annual cadence.
function Indicators({ r }: { r: any }) {
  const off = 'var(--border-strong)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
      <i className="ti ti-receipt-tax" title={r.has_vat ? 'VAT registered' : 'No VAT'} style={{ fontSize: 15, color: r.has_vat ? '#15803D' : off }} />
      <i className="ti ti-percentage" title={r.has_wht ? 'WHT filed' : 'No WHT'} style={{ fontSize: 15, color: r.has_wht ? '#185FA5' : off }} />
      <span title="Employees" style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 12, color: r.employees ? 'var(--text-secondary)' : off }}><i className="ti ti-users" style={{ fontSize: 14 }} />{r.employees || 0}</span>
      {r.service_type === 'monthly' && <i className="ti ti-calendar-month" title="Monthly service" style={{ fontSize: 15, color: '#BA7517' }} />}
      {r.service_type === 'annual' && <i className="ti ti-calendar-stats" title="Annual-only service" style={{ fontSize: 15, color: '#5F5E5A' }} />}
    </span>
  );
}

// Risk flags (P5): documentation + payment, colored by level (med amber, high red). Hover = reason.
function RiskBadges({ r }: { r: any }) {
  const col = (lvl: string) => lvl === 'high' ? '#DC2626' : lvl === 'med' ? '#BA7517' : '';
  if (!r.doc_risk && !r.payment_risk) return null;
  const pill = (icon: string, lvl: string, reason: string) => (
    <span title={reason} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 999, color: '#fff', background: col(lvl) }}>
      <i className={`ti ti-${icon}`} style={{ fontSize: 12 }} />{lvl}
    </span>
  );
  return (
    <span style={{ display: 'inline-flex', gap: 5 }}>
      {r.doc_risk && pill('file-alert', r.doc_risk, r.doc_reason || 'Documentation risk')}
      {r.payment_risk && pill('coin', r.payment_risk, r.payment_reason || 'Payment risk')}
    </span>
  );
}


function TaskStatus({ status, overdue, onSet }: { status: string; overdue: boolean; onSet: (s: string) => void }) {
  const map: Record<string, { bg: string; c: string; l: string }> = {
    pending: overdue ? { bg: '#FEF2F2', c: '#DC2626', l: 'Overdue' } : { bg: '#FFFBEB', c: '#B45309', l: 'Pending' },
    in_progress: { bg: '#E6F1FB', c: '#185FA5', l: 'In progress' },
    blocked: { bg: '#FEF2F2', c: '#DC2626', l: 'Blocked' },
    done: { bg: '#ECFDF5', c: '#15803D', l: 'Done' },
    na: { bg: 'var(--c-gray-100)', c: 'var(--text-tertiary)', l: 'N/A' },
  };
  const s = map[status] || map.pending;
  return (
    <select value={status} onChange={e => onSet(e.target.value)} style={{ padding: '4px 8px', fontSize: 11.5, fontWeight: 700, borderRadius: 8, background: s.bg, color: s.c, border: `1px solid ${s.c}33`, cursor: 'pointer' }}>
      <option value="pending">{overdue ? 'Overdue' : 'Pending'}</option>
      <option value="in_progress">In progress</option>
      <option value="blocked">Blocked</option>
      <option value="done">Done</option>
      <option value="na">N/A</option>
    </select>
  );
}

function Assign({ clientId }: { clientId: string }) {
  const [pros, setPros] = useState<any[]>([]);
  const [assign, setAssign] = useState<any>({ accountant: null, auditor: null });
  const load = () => { Promise.all([api.xproList(), api.xproAssignments(clientId)]).then(([a, b]: any) => { setPros(a.rows || []); setAssign(b || {}); }).catch(() => {}); };
  useEffect(load, [clientId]); // eslint-disable-line
  const set = async (role: string, proId: string) => {
    try {
      if (proId) await api.xproAssign(clientId, role, proId); else await api.xproUnassign(clientId, role);
      load();
    } catch (e: any) { window.alert(e.message); load(); }
  };
  const opts = (role: string) => pros.filter(p => p.role === role || p.role === 'both');
  const sel: React.CSSProperties = { padding: '5px 8px', fontSize: 12, borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer', maxWidth: 200 };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>Ext. accountant</span>
        <select value={assign.accountant?.pro_id || ''} onChange={e => set('accountant', e.target.value)} style={sel}>
          <option value="">— none —</option>
          {opts('accountant').map(p => <option key={p.id} value={p.id}>{p.name}{p.acct_clients != null ? ` (${p.acct_clients})` : ''}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>Ext. auditor</span>
        <select value={assign.auditor?.pro_id || ''} onChange={e => set('auditor', e.target.value)} style={sel}>
          <option value="">— none —</option>
          {opts('auditor').map(p => <option key={p.id} value={p.id}>{p.name}{p.audit_clients != null ? ` (${p.audit_clients})` : ''}</option>)}
        </select>
      </div>
    </div>
  );
}

function ClientTasks({ client, onClose, onChanged }: { client: any; onClose: () => void; onChanged: () => void }) {
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const load = () => { setLoading(true); api.aiwTasks(client.id).then((d: any) => { setTasks(d.tasks || []); }).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, [client.id]); // eslint-disable-line

  const generate = async () => { setBusy(true); try { await api.aiwGenerate(client.id); load(); onChanged(); } finally { setBusy(false); } };
  const setStatus = async (t: any, status: string) => { setTasks(ts => ts.map(x => x.id === t.id ? { ...x, status } : x)); await api.aiwSetTaskStatus(t.id, status).catch(() => load()); onChanged(); };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1500, display: 'flex', justifyContent: 'flex-end' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 820, maxWidth: '96vw', height: '100%', background: 'var(--bg-app)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{client.name}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>{tasks.length} tasks</div>
          </div>
          <button onClick={generate} disabled={busy} style={{ ...btn, background: '#0F6E56', color: '#fff', border: 'none', opacity: busy ? 0.6 : 1 }}>{busy ? 'Generating…' : 'Generate / refresh tasks'}</button>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 18 }}><i className="ti ti-x" /></button>
        </div>
        <Assign clientId={client.id} />
        <Contracts clientId={client.id} />
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
            : tasks.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>No tasks yet — add the client’s services, then click “Generate / refresh tasks”.</div>
              : (
                <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-surface)', borderRadius: 10, overflow: 'hidden' }}>
                  <thead><tr>
                    <th style={th}>Task</th><th style={th}>Cycle</th><th style={th}>Period</th><th style={th}>Due</th><th style={th}>Owner</th><th style={th}>Action</th><th style={{ ...th, width: 130 }}>Status</th>
                  </tr></thead>
                  <tbody>
                    {tasks.map(t => (
                      <tr key={t.id}>
                        <td style={{ ...td, fontWeight: 600 }}>{t.label}</td>
                        <td style={td}>{CYCLE_LABEL[t.cycle] || t.cycle}</td>
                        <td style={{ ...td, color: 'var(--text-tertiary)' }}>{t.period}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: t.overdue ? 700 : 400, color: t.overdue ? '#DC2626' : 'var(--text-primary)' }}>{fmtDate(t.due_date)}</td>
                        <td style={td}>{t.owner_role}</td>
                        <td style={td}>{ACTION_LABEL[t.action_type] || t.action_type}</td>
                        <td style={td}><TaskStatus status={t.status} overdue={t.overdue} onSet={s => setStatus(t, s)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
        </div>
      </div>
    </div>
  );
}

export default function AIWReports({ embedded }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [genBusy, setGenBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [detail, setDetail] = useState<any | null>(null);

  const load = () => { setLoading(true); api.aiwClients().then((d: any) => setRows(d.rows || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const [riskBusy, setRiskBusy] = useState(false);
  const syncAll = async () => {
    setSyncBusy(true);
    try { const r: any = await api.aiwSyncStatusAll(); window.alert(`Synced — ${r.done} tasks marked done from uploaded / filed data.`); load(); }
    finally { setSyncBusy(false); }
  };
  const recomputeRisk = async () => {
    setRiskBusy(true);
    try { await api.aiwRecomputeRisk(); load(); }
    finally { setRiskBusy(false); }
  };

  const generateAll = async () => {
    if (!window.confirm('Generate tasks for ALL accounting clients with services? (idempotent)')) return;
    setGenBusy(true);
    try { const r: any = await api.aiwGenerateAll(); window.alert(`Done — ${r.clients} clients scanned, ${r.created} new tasks created.`); load(); }
    finally { setGenBusy(false); }
  };

  const ql = q.trim().toLowerCase();
  const shown = rows.filter(r => !ql || (r.name || '').toLowerCase().includes(ql));
  const withTasks = rows.filter(r => r.tasks > 0).length;

  return (
    <div style={{ ...(embedded ? { flex: 1, minHeight: 0 } : { height: '100vh' }), display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        {!embedded && <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}><img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} /></button>}
        {!embedded && <div style={{ fontSize: 15, fontWeight: 700 }}>AI Working Reports</div>}
        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{withTasks} / {rows.length} clients with tasks</span>
        <div style={{ flex: 1 }} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search client…" style={{ padding: '7px 11px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 8, background: 'var(--c-gray-50)', width: 200, outline: 'none' }} />
        <button onClick={load} style={btn}><i className="ti ti-refresh" /></button>
        <button onClick={syncAll} disabled={syncBusy} title="Auto-mark tasks done from uploaded / filed data" style={{ ...btn, opacity: syncBusy ? 0.6 : 1 }}><i className="ti ti-refresh-dot" style={{ marginRight: 4 }} />{syncBusy ? 'Syncing…' : 'Sync statuses'}</button>
        <button onClick={recomputeRisk} disabled={riskBusy} title="Recompute documentation / payment risk flags" style={{ ...btn, opacity: riskBusy ? 0.6 : 1 }}><i className="ti ti-alert-triangle" style={{ marginRight: 4 }} />{riskBusy ? '…' : 'Risk'}</button>
        <button onClick={generateAll} disabled={genBusy} style={{ ...btn, background: '#0F6E56', color: '#fff', border: 'none', opacity: genBusy ? 0.6 : 1 }}>{genBusy ? 'Generating…' : 'Generate all'}</button>
      </header>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 18px' }}>
        {loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
          : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-surface)' }}>
                <thead><tr>
                  <th style={th}>Client</th><th style={th}>Stage</th>
                  <th style={{ ...th, textAlign: 'right' }}>Tasks</th><th style={{ ...th, textAlign: 'right' }}>Done</th>
                  <th style={{ ...th, textAlign: 'right' }}>Overdue</th><th style={{ ...th, textAlign: 'right' }}>Blocked</th><th style={{ ...th, width: 40 }} />
                </tr></thead>
                <tbody>
                  {shown.map(r => (
                    <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setDetail(r)} onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums', minWidth: 40 }}>#{String(r.no || 0).padStart(4, '0')}</span>
                          <span style={{ fontWeight: 600 }}>{r.name}</span>
                          <Indicators r={r} />
                          <RiskBadges r={r} />
                        </div>
                      </td>
                      <td style={{ ...td, color: 'var(--text-tertiary)' }}>{r.stage}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{r.tasks}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#15803D' }}>{r.done || ''}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: r.overdue ? 700 : 400, color: r.overdue ? '#DC2626' : 'var(--text-tertiary)' }}>{r.overdue || ''}</td>
                      <td style={{ ...td, textAlign: 'right', color: r.blocked ? '#DC2626' : 'var(--text-tertiary)' }}>{r.blocked || ''}</td>
                      <td style={{ ...td, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-chevron-right" /></td>
                    </tr>
                  ))}
                  {!shown.length && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: 'var(--text-tertiary)', padding: 24 }}>No accounting clients.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {detail && <ClientTasks client={detail} onClose={() => setDetail(null)} onChanged={load} />}
    </div>
  );
}
